/**
 * Dashboard Skeleton
 * Simplified loading state for the conversations dashboard
 *
 * Layout:
 * - Left: ConversationSidebar (w-80)
 *   - Header with title + new message button
 *   - Search bar
 *   - Open/Closed toggle
 *   - Sort & Filter + Unread buttons
 *   - Conversation list (avatar + name + preview)
 * - Right: Chat area (flex-1)
 *   - Simple header with avatar + name + action buttons
 *   - Messages area
 *   - Message input
 */

import React from 'react';
import { Skeleton, SkeletonCircle } from './Skeleton';

/**
 * Single conversation row skeleton
 * Matches ConversationList item structure
 */
const ConversationRowSkeleton: React.FC = () => (
  <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
    {/* Avatar */}
    <SkeletonCircle className="w-10 h-10 flex-shrink-0" />
    {/* Content */}
    <div className="flex-1 min-w-0">
      <div className="flex justify-between items-center mb-1">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-3 w-10" />
      </div>
      <Skeleton className="h-3 w-3/4" />
    </div>
  </div>
);

/**
 * Conversation sidebar skeleton
 * Matches ConversationSidebar component layout
 */
const SidebarSkeleton: React.FC = () => (
  <div className="w-80 flex flex-col flex-shrink-0 border-r border-border bg-card-bg">
    {/* Header: Title + New Message button - matches SidebarHeader */}
    <div className="flex items-center justify-between p-4 pb-3">
      <Skeleton className="h-8 w-40" /> {/* text-2xl title */}
      <Skeleton className="h-7 w-7 rounded-md" /> {/* btn-primary p-1.5 */}
    </div>

    {/* Search bar - matches SearchBar component */}
    <div className="px-4 mb-3">
      <Skeleton className="h-10 w-full rounded-md" />
    </div>

    {/* Open/Closed toggle - matches sliding pill toggle */}
    <div className="px-4 mb-3">
      <Skeleton className="h-9 w-full rounded-md" />
    </div>

    {/* Sort & Filter + Unread buttons - matches ConversationFilters */}
    <div className="px-4 pb-3 flex items-center space-x-2">
      {/* Sort & Filter button (flex-1) */}
      <Skeleton className="h-9 flex-1 rounded-md" />
      {/* Unread toggle button */}
      <Skeleton className="h-9 w-20 rounded-md" />
    </div>

    {/* Conversation list */}
    <div className="flex-1 overflow-hidden px-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <ConversationRowSkeleton key={i} />
      ))}
    </div>
  </div>
);

/**
 * Message bubble skeleton
 * Simplified to match actual MessageBubble structure
 */
const MessageBubbleSkeleton: React.FC<{ isOutgoing?: boolean; width?: string }> = ({
  isOutgoing,
  width = isOutgoing ? 'w-32' : 'w-48'
}) => (
  <div className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'} mb-3`}>
    <div className={`flex items-start gap-2 ${isOutgoing ? 'flex-row-reverse' : ''}`}>
      {!isOutgoing && <SkeletonCircle className="w-10 h-10 flex-shrink-0" />}
      <Skeleton className={`h-9 ${width} rounded-2xl ${isOutgoing ? 'rounded-tr-sm' : 'rounded-tl-sm'}`} />
    </div>
  </div>
);

/**
 * Chat area skeleton
 * Matches ConversationView main content area
 */
const ChatAreaSkeleton: React.FC = () => (
  <div className="flex-1 flex flex-col h-full overflow-hidden min-w-0 bg-card-background relative">
    {/* Conversation header - matches ConversationHeader component */}
    <div className="p-3 bg-card-background min-h-12">
      <div className="flex justify-between items-center">
        {/* Left: Avatar + name */}
        <div className="flex items-center gap-2">
          <SkeletonCircle className="w-8 h-8" />
          <Skeleton className="h-5 w-28" />
        </div>

        {/* Right: Action buttons */}
        <div className="flex items-center space-x-2">
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>
      </div>
    </div>

    {/* Messages area */}
    <div className="flex-1 overflow-hidden p-4 bg-card-background">
      <MessageBubbleSkeleton width="w-40" />
      <MessageBubbleSkeleton isOutgoing width="w-28" />
      <MessageBubbleSkeleton width="w-56" />
      <MessageBubbleSkeleton isOutgoing width="w-36" />
      <MessageBubbleSkeleton width="w-32" />
    </div>

    {/* Message input - matches MessageInput component */}
    <div className="flex-shrink-0 p-4 bg-card-background">
      <Skeleton className="h-12 w-full rounded-xl" />
    </div>
  </div>
);

/**
 * Full dashboard skeleton
 * Use this as the loading state for the main conversations page
 */
export const DashboardSkeleton: React.FC = () => (
  <div className="flex h-full w-full overflow-hidden">
    <SidebarSkeleton />
    <ChatAreaSkeleton />
  </div>
);

/**
 * Chat-only skeleton (when sidebar is already visible)
 * Use this when only the chat area needs a loading state
 */
export const ChatSkeleton: React.FC = () => <ChatAreaSkeleton />;

/**
 * Sidebar-only skeleton
 * Use this when only the sidebar needs a loading state
 */
export const ConversationListSkeleton: React.FC<{ count?: number }> = ({ count = 8 }) => (
  <div className="space-y-1">
    {Array.from({ length: count }).map((_, i) => (
      <ConversationRowSkeleton key={i} />
    ))}
  </div>
);

export default DashboardSkeleton;
